import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type CreateSubPurpose, type UpdateSubPurpose } from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

export const useGetSubPurposesQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['sub-purposes'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/sub-purposes')
      if (error) throwOnError(error)
      return data
    }
  })
}

export const useCreateSubPurposeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (body: CreateSubPurpose) => {
      const { data, error } = await authFetch().POST('/sub-purposes', { body })
      if (error) throwOnError(error)
      return data
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: ['sub-purposes'] })
  })
}

export const useUpdateSubPurposeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      subPurposeId,
      etag,
      body
    }: {
      subPurposeId: number
      etag: string
      body: UpdateSubPurpose
    }) => {
      const { data, error } = await authFetch().PUT(
        '/sub-purposes/{subPurposeId}',
        {
          params: { path: { subPurposeId }, header: { 'If-Match': etag } },
          body
        }
      )
      if (error) throwOnError(error)
      return data
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: ['sub-purposes'] })
  })
}

export const useDeleteSubPurposeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (subPurposeId: number) =>
      authFetch().DELETE('/sub-purposes/{subPurposeId}', {
        params: { path: { subPurposeId } }
      }),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: ['sub-purposes'] })
  })
}
